import React, { useCallback, useEffect, useState } from 'react';
import InspectionToolbar from './Toolbar/inspectionToolbar';
import VehicleInspectionRecordTable from './Table/VehicleInspectionRecordTable';
import Pagination from 'reusable/pagination';
import { PAGE_SIZE } from 'config/constant';
import axiosInstance from 'config/axiosInstance';
import { businessMonth, businessRangeQuery, monthBounds } from 'config/time';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { pageToRecover } from 'reusable/pageRecovery';
import { fetchAllPages } from 'reusable/fetchAllPages';
import { ExportRange } from 'reusable/ExportRangeDialog';

const VehicleInspectionManagement: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [inspectionRecordData, setInspectionRecordData] = useState<any[]>([]);
  // A28（2026-11-03 和田様の UAT）。表の外の月の切り替えで選んだ月（`YYYY-MM`）。表はその月の分をページ送り。
  const [month, setMonth] = useState(businessMonth);
  const [failedInspection, setFailedInspection] = useState<boolean | null>(
    null
  );
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  const handleMonthChange = (value: string) => {
    setMonth(value);
    setCurrentPage(1);
  };

  const handleFailedInspectionChange = (value: boolean | null) => {
    setFailedInspection(value);
    setCurrentPage(1);
  };

  // サーバーの条件名で送る。以前は pageSize・sort・startDate/endDate を送っており、
  // 期間・並び替え・1ページの件数が効いていなかった。range が null なら期間で絞らない（エクスポートの全件）。
  const listUrl = useCallback(
    (range: ExportRange, page: number, pageSize: number) => {
      let apiUrl = '/v1/vehicle_inspection_records/?';
      if (range) apiUrl += businessRangeQuery('create_time', range.start, range.end);
      apiUrl += `page=${page}&page_size=${pageSize}&search=${searchValue}&order_by=${sortValue}`;
      if (failedInspection !== null) {
        apiUrl += `&inspecion_result=${failedInspection}`;
      }
      return apiUrl;
    },
    [searchValue, sortValue, failedInspection]
  );

  // A28。エクスポートは選んだ範囲を全ページ読む（並び・絞り込みは一覧と同じ）。
  const fetchExportRows = useCallback(
    (range: ExportRange) => fetchAllPages((page, pageSize) => listUrl(range, page, pageSize)),
    [listUrl]
  );

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      const response = await axiosInstance.get(listUrl(monthBounds(month), currentPage, PAGE_SIZE));
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      if (!isCurrent()) return;
      setInspectionRecordData(response.data.results);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, PAGE_SIZE, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch inspection records', error);
      setFetchError(error);
    }
  }, [listUrl, currentPage, month, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <section className="flex flex-col space-y-4">
      {/* Toolbar */}
      <div>
        <InspectionToolbar
          searchValue={searchValue}
          sortValue={sortValue}
          currentPage={currentPage}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          month={month}
          onMonthChange={handleMonthChange}
          onFailedInspectionChange={handleFailedInspectionChange}
          fetchExportRows={fetchExportRows}
        />
      </div>
      {/* Table */}
      <div>
        {fetchError ? (
          <FetchFailed error={fetchError} onRetry={fetchData} />
        ) : (
          <VehicleInspectionRecordTable inspectionRecord={inspectionRecordData} />
        )}
      </div>
      {/* Pagination */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </section>
  );
};

export default VehicleInspectionManagement;
